import React, { useState, useMemo } from 'react';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Shield, Info, X, ChevronRight, Eye, EyeOff } from 'lucide-react';

// Type workaround for Recharts components with strict TypeScript
const TypedPolarAngleAxis = PolarAngleAxis as any;
const TypedPolarRadiusAxis = PolarRadiusAxis as any;
const TypedPolarGrid = PolarGrid as any;
const TypedRadar = Radar as any;
const TypedTooltip = Tooltip as any;

// Types
export interface ComplianceArea {
  key: string;
  label: string;
  fullLabel: string;
  description: string;
  cfrReference?: string;
}

export interface ContractComplianceData {
  contractId: string;
  contractName: string;
  state: string;
  color: string;
  scores: Record<string, number>; // key -> score (0-100)
  gaps?: Record<string, ComplianceGap[]>; // key -> gaps
}

export interface ComplianceGap {
  id: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  recommendation?: string;
}

export interface RegulatoryAlignmentRadarProps {
  contracts: ContractComplianceData[];
  complianceAreas?: ComplianceArea[];
  onAreaClick?: (area: ComplianceArea, contract: ContractComplianceData) => void;
  onViewGaps?: (area: ComplianceArea, contract: ContractComplianceData) => void;
  testId?: string;
}

// Default CMS compliance areas based on 42 CFR 438
const DEFAULT_COMPLIANCE_AREAS: ComplianceArea[] = [
  {
    key: 'network_adequacy',
    label: 'Network',
    fullLabel: 'Network Adequacy',
    description: 'Provider network standards including time and distance requirements',
    cfrReference: '42 CFR 438.68, 438.206',
  },
  {
    key: 'timely_access',
    label: 'Access',
    fullLabel: 'Timely Access',
    description: 'Appointment availability and wait time standards',
    cfrReference: '42 CFR 438.206(c)(1)',
  },
  {
    key: 'quality',
    label: 'Quality',
    fullLabel: 'Quality Assurance',
    description: 'Quality assessment and performance improvement requirements',
    cfrReference: '42 CFR 438.330',
  },
  {
    key: 'grievance',
    label: 'Grievance',
    fullLabel: 'Grievance & Appeals',
    description: 'Member grievance and appeals process requirements',
    cfrReference: '42 CFR 438.400-438.424',
  },
  {
    key: 'utilization',
    label: 'UM',
    fullLabel: 'Utilization Management',
    description: 'Prior authorization and utilization review requirements',
    cfrReference: '42 CFR 438.210',
  },
  {
    key: 'mlr',
    label: 'MLR',
    fullLabel: 'Medical Loss Ratio',
    description: 'Medical loss ratio calculation and reporting requirements',
    cfrReference: '42 CFR 438.8',
  },
];

// Helper function to get severity color - using design system colors
const getSeverityColor = (severity: string): string => {
  switch (severity) {
    case 'critical':
      return 'text-danger-700 bg-danger-100';
    case 'high':
      return 'text-warning-700 bg-warning-100';
    case 'medium':
      return 'text-warning-600 bg-warning-100';
    case 'low':
      return 'text-primary-600 bg-primary-100';
    default:
      return 'text-neutral-600 bg-neutral-100';
  }
};

// Custom tooltip component
interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  complianceAreas: ComplianceArea[];
  contracts: ContractComplianceData[];
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  complianceAreas,
  contracts,
}) => {
  if (!active || !payload || !label) return null;

  const area = complianceAreas.find((a) => a.label === label);
  if (!area) return null;

  return (
    <div
      className="chart-tooltip min-w-[280px] max-w-[350px]"
      data-testid="radar-tooltip"
    >
      <div className="mb-3">
        <h4 className="chart-tooltip-header">{area.fullLabel}</h4>
        <p className="chart-tooltip-label">{area.cfrReference}</p>
      </div>

      <p className="text-sm text-neutral-600 mb-3">{area.description}</p>

      <div className="chart-tooltip-divider" />

      <div className="space-y-2">
        {payload.map((entry, index) => {
          const contract = contracts.find((c) => c.contractName === entry.name);
          return (
            <div
              key={index}
              className="chart-tooltip-item p-2 rounded"
              style={{ backgroundColor: `${entry.color}15` }}
            >
              <div className="chart-tooltip-item-label">
                <div
                  className="chart-legend-dot"
                  style={{ backgroundColor: entry.color }}
                />
                <span>
                  {contract?.state || entry.name}
                </span>
              </div>
              <span className="chart-tooltip-item-value" style={{ color: entry.color }}>
                {entry.value}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Detail modal for area drill-down
interface AreaDetailModalProps {
  area: ComplianceArea;
  contracts: ContractComplianceData[];
  onClose: () => void;
  onViewGaps?: (area: ComplianceArea, contract: ContractComplianceData) => void;
}

const AreaDetailModal: React.FC<AreaDetailModalProps> = ({
  area,
  contracts,
  onClose,
  onViewGaps,
}) => {
  return (
    <div
      className="chart-modal-overlay"
      onClick={onClose}
      data-testid="area-detail-modal"
    >
      <div
        className="chart-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="chart-modal-header">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold text-neutral-900">{area.fullLabel}</h3>
              <p className="text-sm text-neutral-500 mt-1">{area.cfrReference}</p>
            </div>
            <button
              onClick={onClose}
              className="btn-ghost p-2 rounded-lg"
              data-testid="close-modal"
            >
              <X className="h-5 w-5 text-neutral-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="chart-modal-content">
          <p className="text-neutral-600 mb-6">{area.description}</p>

          {/* Contract Scores and Gaps */}
          <div className="space-y-6">
            {contracts.map((contract) => {
              const score = contract.scores[area.key] || 0;
              const gaps = contract.gaps?.[area.key] || [];
              const gapCountBySeverity = gaps.reduce(
                (acc, gap) => {
                  acc[gap.severity] = (acc[gap.severity] || 0) + 1;
                  return acc;
                },
                {} as Record<string, number>
              );

              return (
                <div
                  key={contract.contractId}
                  className="bg-neutral-50 rounded-lg p-4"
                  data-testid={`contract-detail-${contract.contractId}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="chart-legend-dot"
                        style={{ backgroundColor: contract.color }}
                      />
                      <div>
                        <h4 className="font-medium text-neutral-900">{contract.contractName}</h4>
                        <p className="text-sm text-neutral-500">{contract.state}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className="text-2xl font-bold"
                        style={{ color: contract.color }}
                      >
                        {score}%
                      </div>
                      <div className="text-xs text-neutral-500">Compliance Score</div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="score-progress mb-4">
                    <div
                      className="score-progress-fill"
                      style={{
                        width: `${score}%`,
                        backgroundColor: contract.color,
                      }}
                    />
                  </div>

                  {/* Gap summary */}
                  {gaps.length > 0 ? (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-neutral-700">
                          {gaps.length} Gap{gaps.length !== 1 ? 's' : ''} Identified
                        </span>
                        <div className="flex items-center gap-2">
                          {gapCountBySeverity.critical > 0 && (
                            <span className="chart-badge-critical">
                              {gapCountBySeverity.critical} Critical
                            </span>
                          )}
                          {gapCountBySeverity.high > 0 && (
                            <span className="chart-badge-high">
                              {gapCountBySeverity.high} High
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Top gaps preview */}
                      <ul className="space-y-2 mb-3">
                        {gaps.slice(0, 3).map((gap) => (
                          <li
                            key={gap.id}
                            className="flex items-start gap-2 text-sm"
                            data-testid={`gap-${gap.id}`}
                          >
                            <span
                              className={`px-1.5 py-0.5 text-xs font-medium rounded ${getSeverityColor(
                                gap.severity
                              )}`}
                            >
                              {gap.severity}
                            </span>
                            <span className="text-neutral-700 line-clamp-1">{gap.description}</span>
                          </li>
                        ))}
                        {gaps.length > 3 && (
                          <li className="text-xs text-neutral-500 pl-2">
                            +{gaps.length - 3} more gaps
                          </li>
                        )}
                      </ul>

                      {onViewGaps && (
                        <button
                          onClick={() => onViewGaps(area, contract)}
                          className="btn-link text-sm"
                          data-testid={`view-gaps-${contract.contractId}`}
                        >
                          <ChevronRight className="h-4 w-4" />
                          View All Gaps
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-success-600">
                      <Shield className="h-4 w-4" />
                      <span>No compliance gaps identified</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="chart-modal-footer">
          <button
            onClick={onClose}
            className="btn-secondary w-full"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Legend component with toggle capability
interface RadarLegendProps {
  contracts: ContractComplianceData[];
  visibleContracts: Set<string>;
  onToggleContract: (contractId: string) => void;
}

const RadarLegend: React.FC<RadarLegendProps> = ({
  contracts,
  visibleContracts,
  onToggleContract,
}) => {
  return (
    <div className="flex flex-wrap items-center justify-center gap-4" data-testid="radar-legend">
      {contracts.map((contract) => {
        const isVisible = visibleContracts.has(contract.contractId);
        return (
          <button
            key={contract.contractId}
            onClick={() => onToggleContract(contract.contractId)}
            className={isVisible ? 'chart-legend-button-active' : 'chart-legend-button-inactive'}
            data-testid={`legend-item-${contract.contractId}`}
          >
            <div
              className={`chart-legend-dot transition-opacity ${
                isVisible ? 'opacity-100' : 'opacity-40'
              }`}
              style={{ backgroundColor: contract.color }}
            />
            <span
              className={`text-sm font-medium ${
                isVisible ? 'text-neutral-700' : 'text-neutral-400'
              }`}
            >
              {contract.state}
            </span>
            {isVisible ? (
              <Eye className="h-4 w-4 text-neutral-400" />
            ) : (
              <EyeOff className="h-4 w-4 text-neutral-400" />
            )}
          </button>
        );
      })}
    </div>
  );
};

// Score comparison table
interface ScoreComparisonTableProps {
  contracts: ContractComplianceData[];
  complianceAreas: ComplianceArea[];
  onAreaClick: (area: ComplianceArea) => void;
}

const ScoreComparisonTable: React.FC<ScoreComparisonTableProps> = ({
  contracts,
  complianceAreas,
  onAreaClick,
}) => {
  return (
    <div className="overflow-x-auto scrollbar-thin" data-testid="score-comparison-table">
      <table className="score-table">
        <thead>
          <tr className="border-b border-neutral-200">
            <th className="text-left">Compliance Area</th>
            {contracts.map((contract) => (
              <th
                key={contract.contractId}
                className="text-center"
                style={{ color: contract.color }}
              >
                {contract.state}
              </th>
            ))}
            <th className="text-center">Difference</th>
          </tr>
        </thead>
        <tbody>
          {complianceAreas.map((area) => {
            const scores = contracts.map((c) => c.scores[area.key] || 0);
            const maxScore = Math.max(...scores);
            const minScore = Math.min(...scores);
            const diff = maxScore - minScore;

            return (
              <tr
                key={area.key}
                onClick={() => onAreaClick(area)}
                data-testid={`table-row-${area.key}`}
              >
                <td>
                  <div className="font-medium text-neutral-900">{area.fullLabel}</div>
                  <div className="text-xs text-neutral-500">{area.cfrReference}</div>
                </td>
                {contracts.map((contract) => {
                  const score = contract.scores[area.key] || 0;
                  const isHighest = score === maxScore && scores.filter((s) => s === maxScore).length === 1;
                  const isLowest = score === minScore && diff > 5;

                  return (
                    <td
                      key={contract.contractId}
                      className="text-center"
                    >
                      <span
                        className={`score-value ${
                          isHighest
                            ? 'score-value-high'
                            : isLowest
                            ? 'score-value-low'
                            : ''
                        }`}
                      >
                        {score}%
                      </span>
                    </td>
                  );
                })}
                <td className="text-center">
                  {diff > 0 ? (
                    <span
                      className={`score-diff ${
                        diff > 15
                          ? 'score-diff-high'
                          : diff > 5
                          ? 'score-diff-medium'
                          : 'score-diff-low'
                      }`}
                    >
                      {diff}pt
                    </span>
                  ) : (
                    <span className="text-neutral-400">-</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Main component
export const RegulatoryAlignmentRadar: React.FC<RegulatoryAlignmentRadarProps> = ({
  contracts,
  complianceAreas = DEFAULT_COMPLIANCE_AREAS,
  onAreaClick,
  onViewGaps,
  testId = 'regulatory-alignment-radar',
}) => {
  const [visibleContracts, setVisibleContracts] = useState<Set<string>>(
    new Set(contracts.map((c) => c.contractId))
  );
  const [selectedArea, setSelectedArea] = useState<ComplianceArea | null>(null);
  const [showTable, setShowTable] = useState(false);

  // Transform data for radar chart
  const chartData = useMemo(() => {
    return complianceAreas.map((area) => {
      const dataPoint: Record<string, string | number> = {
        subject: area.label,
        fullSubject: area.fullLabel,
      };

      contracts.forEach((contract) => {
        dataPoint[contract.contractName] = contract.scores[area.key] || 0;
      });

      return dataPoint;
    });
  }, [contracts, complianceAreas]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    return contracts.map((contract) => {
      const scores = Object.values(contract.scores);
      const avgScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
      const lowestArea = complianceAreas.reduce(
        (lowest, area) => {
          const score = contract.scores[area.key] || 0;
          if (score < lowest.score) {
            return { area, score };
          }
          return lowest;
        },
        { area: complianceAreas[0], score: 100 }
      );

      return {
        ...contract,
        avgScore,
        lowestArea: lowestArea.area,
        lowestScore: lowestArea.score,
      };
    });
  }, [contracts, complianceAreas]);

  const handleToggleContract = (contractId: string) => {
    setVisibleContracts((prev) => {
      const next = new Set(prev);
      if (next.has(contractId)) {
        // Don't allow hiding all contracts
        if (next.size > 1) {
          next.delete(contractId);
        }
      } else {
        next.add(contractId);
      }
      return next;
    });
  };

  const handleAreaClick = (area: ComplianceArea) => {
    setSelectedArea(area);
    if (onAreaClick && contracts.length > 0) {
      onAreaClick(area, contracts[0]);
    }
  };

  const handleChartClick = (data: any) => {
    if (data && data.activeLabel) {
      const area = complianceAreas.find((a) => a.label === data.activeLabel);
      if (area) {
        handleAreaClick(area);
      }
    }
  };

  if (contracts.length === 0) {
    return (
      <div
        className="chart-container chart-empty"
        data-testid={`${testId}-empty`}
      >
        <div className="chart-empty-icon">
          <Shield className="h-8 w-8 text-neutral-400" />
        </div>
        <h3 className="chart-empty-title">No Compliance Data Available</h3>
        <p className="chart-empty-description">
          Run a compliance analysis on your contracts to view the regulatory alignment radar.
        </p>
      </div>
    );
  }

  return (
    <div className="chart-container" data-testid={testId}>
      {/* Header */}
      <div className="chart-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="chart-title">
              <Shield className="h-5 w-5 text-primary-500" />
              Regulatory Alignment Radar
            </h3>
            <p className="chart-subtitle">
              CMS compliance comparison across key regulatory areas
            </p>
          </div>

          {/* View toggle */}
          <div className="chart-view-toggle">
            <button
              onClick={() => setShowTable(false)}
              className={!showTable ? 'chart-view-toggle-btn-active' : 'chart-view-toggle-btn-inactive'}
              data-testid="view-chart"
            >
              Chart
            </button>
            <button
              onClick={() => setShowTable(true)}
              className={showTable ? 'chart-view-toggle-btn-active' : 'chart-view-toggle-btn-inactive'}
              data-testid="view-table"
            >
              Table
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          {summaryStats.map((stat) => (
            <div
              key={stat.contractId}
              className="compliance-summary-card"
              data-testid={`summary-${stat.contractId}`}
            >
              <div
                className="compliance-summary-score"
                style={{ backgroundColor: stat.color }}
              >
                {stat.avgScore}
              </div>
              <div className="compliance-summary-info">
                <div className="compliance-summary-title">{stat.state}</div>
                <div className="compliance-summary-detail">
                  Lowest: {stat.lowestArea.label} ({stat.lowestScore}%)
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="chart-content">
        {!showTable ? (
          <>
            {/* Radar Chart */}
            <div className="radar-chart-container mb-6" data-testid="radar-chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart
                  data={chartData}
                  onClick={handleChartClick}
                  style={{ cursor: 'pointer' }}
                >
                  <TypedPolarGrid stroke="#e5e5e5" />
                  <TypedPolarAngleAxis
                    dataKey="subject"
                    tick={{ fill: '#525252', fontSize: 12, fontWeight: 500 }}
                    tickLine={false}
                  />
                  <TypedPolarRadiusAxis
                    angle={30}
                    domain={[0, 100]}
                    tick={{ fill: '#a3a3a3', fontSize: 10 }}
                    axisLine={false}
                    tickCount={5}
                  />
                  {contracts.map(
                    (contract) =>
                      visibleContracts.has(contract.contractId) && (
                        <TypedRadar
                          key={contract.contractId}
                          name={contract.contractName}
                          dataKey={contract.contractName}
                          stroke={contract.color}
                          fill={contract.color}
                          fillOpacity={0.2}
                          strokeWidth={2}
                          dot={{
                            r: 4,
                            fill: contract.color,
                            strokeWidth: 2,
                            stroke: '#fff',
                          }}
                          activeDot={{
                            r: 6,
                            fill: contract.color,
                            strokeWidth: 2,
                            stroke: '#fff',
                          }}
                        />
                      )
                  )}
                  <TypedTooltip
                    content={
                      <CustomTooltip
                        complianceAreas={complianceAreas}
                        contracts={contracts}
                      />
                    }
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Legend */}
            <RadarLegend
              contracts={contracts}
              visibleContracts={visibleContracts}
              onToggleContract={handleToggleContract}
            />

            {/* Click hint */}
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-neutral-500">
              <Info className="h-4 w-4" />
              <span>Click on a compliance area to view detailed breakdown</span>
            </div>
          </>
        ) : (
          /* Table View */
          <ScoreComparisonTable
            contracts={contracts}
            complianceAreas={complianceAreas}
            onAreaClick={handleAreaClick}
          />
        )}
      </div>

      {/* Area Detail Modal */}
      {selectedArea && (
        <AreaDetailModal
          area={selectedArea}
          contracts={contracts}
          onClose={() => setSelectedArea(null)}
          onViewGaps={onViewGaps}
        />
      )}
    </div>
  );
};

export default RegulatoryAlignmentRadar;
